import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
// import { firestore } from 'firebase';
import * as firebase from 'firebase/app';
// import 'firebase/firestore';

import { take } from 'rxjs/operators';
import { transformObservableToSinglePromise } from '../helpers/angular-helpers';
import { jsonCopyObject } from 'globals/helpers/helpers';
import { FilterGroup, filtersFeature } from '../store/filters.era';
import { makeDefaultShareOSSInfoExcludedFields, ShareableItem, ShareOSSInfo, ShareStatus } from 'globals/store/share.data';
import { LogService } from './log.service';
import { ShareService } from './share.service';
import { deleteDoc, doc, Firestore, serverTimestamp, setDoc, updateDoc } from '@angular/fire/firestore';
import { shareActions, shareFeature } from '../store/share.era';
import { lastValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ShareOnsiteService {
  readonly _file = 'share.onsite.service.ts'

  constructor(
    private db: Firestore,
    private log: LogService,
    public store: Store,
    private shareService: ShareService,
  ) { }

  private async updateShareOSSView(id: string) {
    const singleShareOSSInfo = await this.getShareOSSInfo(id, 'shared')
    await this.upsertShareOSSInfo(singleShareOSSInfo)
  }

  async insertShareOSSInfo(singleShareOSSInfo: ShareOSSInfo) {
    console.log({ singleShareOSSInfo })
    const insertObject: ShareableItem = {
      type:'oss',
      code:singleShareOSSInfo.id,
      info:singleShareOSSInfo,
      eventId: this.shareService.eventId,
      timestamp: serverTimestamp()
    }
    await setDoc(doc(this.db, '/shareables/' + singleShareOSSInfo.id), insertObject)
  }

  async addNewShareOSSView(title: string, description: string) {
    const id = await this.shareService.createSlugCode()
    const singleShareOSSInfo = await this.getShareOSSInfo(id, 'never shared', title)
    singleShareOSSInfo.description = description
    try {
      await this.insertShareOSSInfo(singleShareOSSInfo)
    } catch (error) {
      this.log.error({
        file:this._file,
        function:'addNewShareOSSView -> upsertPublishInfo',
        message:'There was an error updating the event',
        error
      })
    }
  }

  public async updateMultipleShareOSSViews(ids: string[]): Promise<boolean> {
    try {
      for (let i = 0; i < ids.length; i++) { // still need an async forEach
        await this.updateShareOSSView(ids[i])
      }
    } catch (error) {
      this.log.error({
        file: this._file,
        function: 'updateMultipleShareOSSViews',
        message: 'There was an error updating the shared timelines',
        error
      })
      return false
    }
    return true
  }

  async getShareOSSInfo(id: string, status?: ShareStatus, title?: string, description?: string): Promise<ShareOSSInfo> {
    const currentInfo = await this.getCurrentShareOSSInfo(id)
    const defaultExcludedFields = makeDefaultShareOSSInfoExcludedFields();
    const def = currentInfo ? currentInfo : {id:'',title:'',status:'never shared',description:'',filters:null, excludedFieldIds:defaultExcludedFields} as ShareOSSInfo
    return Promise.resolve({
      id,
      title: (title || def.title),
      status: (status || def.status),
      description: (description || def.description) || '',
      filters: (def ? def.filters : null) || null,
      excludedFieldIds: !def ? defaultExcludedFields : def.excludedFieldIds || defaultExcludedFields,
    })
  }

  shareOSSTimeline(id: string) {
    return new Promise<any>(async (resolve, reject) => {
      try {
        const newInfo = await this.getShareOSSInfo(id, 'shared')
        await this.upsertShareOSSInfo(newInfo)
        resolve(true)
      } catch (error) {
        this.log.error({
          file: this._file,
          function: 'shareOSSTimeline',
          message: 'There was an error sharing the event',
          error
        })
        return reject(error)
      }
    })
  }

  async getCurrentShareOSSInfo(id: string) {
    try {
      const shareOSSInfos = await lastValueFrom(this.store.select(shareFeature.selectOss).pipe(take(1)))
      const shareOSSInfo = shareOSSInfos ? shareOSSInfos[id] : null
      return shareOSSInfo
    } catch (error) {
      console.log({ error })
      return null
    }
  }

  async upsertShareOSSInfo(singleShareOSSInfo: ShareOSSInfo) {
    return await this.shareService.upsertShareInfo({ info: singleShareOSSInfo }, singleShareOSSInfo.id)
  }

  async duplicateShareOSSView(eventId: string, shareOSSInfo: ShareOSSInfo, title: string) {
    try {
      const newShareOSSInfo: ShareOSSInfo = { ...jsonCopyObject(shareOSSInfo), title, status: 'never shared', id: '_replace_with_code_' } //
      await this.shareService.createShareable('oss', eventId, newShareOSSInfo)
    } catch (error) {
      this.log.error({
        file: this._file,
        function: 'duplicateShareOSSView -> createShareable',
        message: 'There was an error updating the event',
        error
      })
    }
  }

  async addShareOSSFilters(shareOSSInfo: ShareOSSInfo) {
    // this.store.select('filters', 'onsiteShare')
    try {
      const filters = await transformObservableToSinglePromise<FilterGroup>(this.store.select(filtersFeature.selectOnsiteShare))
      const newInfo = { ...shareOSSInfo, filters }
      this.store.dispatch(shareActions.loadShareableOss(newInfo.id, newInfo))
      // await this.upsertPublishInfo(newInfo) // don't save just update the slice
    } catch (error) {
      this.log.error({
        file: this._file,
        function: 'addShareOSSFilters',
        message: 'There was an error updating the event',
        error
      })
    }
  }

  async addExcludedOSSFields(shareOSSInfo: ShareOSSInfo, excludedFieldIds: string[]) {
    try {
      const newInfo = { ...shareOSSInfo, excludedFieldIds }
      this.store.dispatch(shareActions.loadShareableOss(newInfo.id, newInfo))
      // await this.upsertPublishInfo(newInfo)
    } catch (error) {
      this.log.error({
        file:this._file,
        function:'addExcludedOSSFields',
        message:'There was an error updating the event',
        error
      })
    }
  }

  async changeShareOSSViewDescription(shareOSSInfo: ShareOSSInfo, desc: string) {
    try {
      shareOSSInfo = jsonCopyObject(shareOSSInfo)
      shareOSSInfo.description = desc
      await this.upsertShareOSSInfo(shareOSSInfo)
    } catch (error) {
      this.log.error({
        file:this._file,
        function:'changeShareOSSViewDescription',
        message:'There was an error updating the event',
        error
      })
    }
  }

  async changeExcludedOSSShareInfoFields(shareOSSInfo: ShareOSSInfo, excludedFieldIds: string[]) {
    try {
      shareOSSInfo = jsonCopyObject(shareOSSInfo)
      shareOSSInfo.excludedFieldIds = excludedFieldIds
      await this.upsertShareOSSInfo(shareOSSInfo)
    } catch (error) {
      this.log.error({
        file:this._file,
        function:'changeExcludedOSSShareInfoFields',
        message:'There was an error updating the event',
        error
      })
    }
  }

  async changeOSSShareFilters(shareOSSInfo: ShareOSSInfo) {
    try {
      shareOSSInfo = jsonCopyObject(shareOSSInfo)
      const filters = await transformObservableToSinglePromise<FilterGroup>(this.store.select(filtersFeature.selectOnsiteShare))
      // console.log({ filters })
      delete filters.search // can be undefined
      shareOSSInfo.filters = filters
      await this.upsertShareOSSInfo(shareOSSInfo)
    } catch (error) {
      this.log.error({
        file:this._file,
        function:'changeOSSShareFilters',
        message:'There was an error updating the event',
        error
      })
    }
  }

  async changeShareOSSViewNameAndDescription(shareOSSInfo: ShareOSSInfo, title: string, description: string) {
    try {
      shareOSSInfo = jsonCopyObject(shareOSSInfo)
      shareOSSInfo.title = title
      shareOSSInfo.description = description
      await this.upsertShareOSSInfo(shareOSSInfo)
    } catch (error) {
      this.log.error({
        file:this._file,
        function:'changeShareOSSViewName',
        message:'There was an error updating the event',
        error
      })
    }
  }

  async changeShareOSSViewName(shareOSSInfo: ShareOSSInfo, title: string) {
    try {
      shareOSSInfo = jsonCopyObject(shareOSSInfo)
      shareOSSInfo.title = title
      await this.upsertShareOSSInfo(shareOSSInfo)
    } catch (error) {
      this.log.error({
        file:this._file,
        function:'changeShareOSSViewName',
        message:'There was an error updating the event',
        error
      })
    }
  }

  async unshareOSSTimeline(id: string) {
    return new Promise<any>(async (resolve, reject) => {
      try {
        const updateObject = {
          info: await this.getShareOSSInfo(id, 'unshared')
        }
        await updateDoc(doc(this.db, '/shareables/' + id), updateObject)
        resolve(false)
      } catch (error) {
        console.error({ error })
        this.log.error({
          file: this._file,
          function: 'unshareOSSTimeline',
          message: 'There was an error unsharing the event',
          error
        })
        reject(error)
      }
    })
  }

  async deleteShareOSSViews(shareOSSInfos: ShareOSSInfo[]) {
    try {
      for (let i = 0; i < shareOSSInfos.length; i++) { // need a normal for loop or create an async forEach method
        if (shareOSSInfos[i].status === 'shared') await this.unshareOSSTimeline(shareOSSInfos[i].id)
        await deleteDoc(doc(this.db, '/shareables/' + shareOSSInfos[i].id));
      }
    } catch (error) {
      console.log({ error }) // <== why does unpublishing the event from the table component throw a FIREBASE error, whereas doing it from the publish view does not. Are permissions passed from the client?  The permission to delete a publish view should not come from the client but rather from database rules.
      this.log.error({
        file: this._file,
        function: 'deleteShareOSSViews',
        message: 'There was an error unsharing the event',
        error
      })
    }

  }

}
